import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import c from 'classnames';
import ReactModal, { Props as ReactModalProps } from 'react-modal';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import {
  BodyScrollOptions,
  useBodyScrollLock,
} from '../../../../hooks/useBodyScrollLock';
import { useVisualViewport } from '../../../../hooks/useVisualViewport';
import { PopupContext } from '../../../popups/PopupContext';
import ModalCloseContext from '../../context';
import ModalCloseButton from '../ModalCloseButton';

import s from './ModalRoot.scss';

export interface Props
  extends Pick<
    ReactModalProps,
    'isOpen' | 'shouldCloseOnOverlayClick' | 'contentLabel' | 'preventScroll'
  > {
  className?: string;
  width?: number | string;
  height?: number | string;
  /** You need this if you are using the keyboard inside the modal
   * and want the bottom buttons on screen as you type and it should
   * also help with scrolling issues */
  useFixedMobileHeight?: boolean;
  /** By default, modals on mobile are full screen height.
   * Be careful when turning it off, because safari sucks.
   * Don't mess with it for any content that may need to scroll!
   * Also see useFixedMobileHeight */
  isFullscreen?: boolean;
  /** No overlay */
  useTransparentOverlay?: boolean;
  /** Allow mouse to propagate */
  dontCaptureMouseOverlay?: boolean;
  // Redefining this because ReactModalProps marks `event` as required when it really isn't.
  onRequestClose?: (event?: React.MouseEvent | React.KeyboardEvent) => void;
  /** You probably need this if your scrollable inside the modal isn't working */
  bodyScrollLockOptions?: BodyScrollOptions;
  /** Set to false to remove close button. Defaults to true. */
  hasCloseButton?: boolean;
  /** Enable Ipad hacks */
  ipadMode?: boolean;
}

/**
 * The root component of any modal. This is a wrapper for `react-popper`'s `<ReactPopper />` component.
 *
 * Provides theming, body scroll locking, and some additional styling.
 */
const ModalRoot: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  useTransparentOverlay = false,
  dontCaptureMouseOverlay = false,
  hasCloseButton = true,
  width,
  height,
  useFixedMobileHeight = false,
  isFullscreen: isFullscreenProp,
  bodyScrollLockOptions,
  onRequestClose,
  children,
  ipadMode,
  ...rest
}) => {
  const isMobile = useContext(MobileContext);
  const isFullscreen = isFullscreenProp ?? isMobile;

  // Hack the mobile body class for Ipads
  useEffect(() => {
    const isIpad =
      navigator.userAgent.includes('Mac') && 'ontouchend' in document;

    if (
      typeof document !== 'undefined' &&
      ipadMode &&
      isIpad &&
      !document.body.classList.contains('mobile')
    ) {
      document.body.classList.add('mobile');
      document.body.classList.add('mobile-ipad');
    }
    return () => {
      if (
        typeof document !== 'undefined' &&
        ipadMode &&
        isIpad &&
        document.body.classList.contains('mobile-ipad')
      ) {
        document.body.classList.remove('mobile');
      }
    };
  }, [ipadMode]);

  // Ensure removal of body class on close
  // https://github.com/reactjs/react-modal/issues/888
  useEffect(() => {
    return () => {
      try {
        // Check for portals that are not empty and take that as hint we have more modals open
        const modalPortals = document.querySelectorAll('.ReactModalPortal');
        const nonEmptyModals = Array.from(modalPortals).filter(
          modal => modal.innerHTML.trim() !== ''
        ).length;

        if (nonEmptyModals > 0) {
          return;
        }

        document.body.classList.remove('ReactModal__Body--open');
      } catch (e) {}
    };
  }, []);

  // Mobile fixed to viewport fixes
  const [visualViewport, setVisualViewport] = useState<
    VisualViewport | undefined
  >();
  const viewportHandler = useCallback((v: VisualViewport) => {
    setVisualViewport(v);
  }, []);
  useVisualViewport(viewportHandler, isMobile && useFixedMobileHeight);

  const style: { content: React.CSSProperties } = {
    content: {
      width,
      height,
    },
  };
  const contentStyle: React.CSSProperties = {};

  // Apply fixed height for mobile if requested, helper for fixing Safari being lame
  if (isMobile && visualViewport && !height) {
    style.content.height = visualViewport.height;
    style.content.transform = `translate(${visualViewport.offsetLeft}px,${visualViewport.offsetTop}px)`;
    style.content.scale = visualViewport.scale;
    // Calculate positions from top left
    style.content.top = 0;
    if (isFullscreen) {
      style.content.left = 0;
    }
    style.content.position = 'fixed';
    // Set the max height of the content, fixes content expanding beyond viewport
    contentStyle.maxHeight = visualViewport.height;
  }

  // Mobile body scroll locking - Important makes IOS scroll
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { onLock, onUnlock, setScrollableRef, scrollableElementRef } =
    useBodyScrollLock(bodyScrollLockOptions);

  useEffect(() => {
    const focusedEl = document.activeElement;
    const contentEl = contentRef.current;
    const isFocused = el => el === focusedEl || el.contains(focusedEl);
    const needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
    let focusTimeout;

    if (needsFocus && contentEl) {
      // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
      focusTimeout = setTimeout(() => contentEl.focus(), 100);
    }

    return () => focusTimeout && clearTimeout(focusTimeout);
  }, []);

  const setContentRef = (instance: HTMLDivElement | null) => {
    contentRef.current = instance;

    // Important makes IOS scroll
    // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
    // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
    // This is actually the fallback, in general you have ModalLayout which will set the default scrollale below.
    if (!scrollableElementRef.current) {
      setScrollableRef(instance);
    }
  };

  const setDefaultScrollRef = (instance: HTMLDivElement | null) => {
    // Important makes IOS scroll
    // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
    // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
    // This is the default when using ModalLayout, there is a fallback above which is the the ReactModal content.
    if (!scrollableElementRef.current) {
      setScrollableRef(instance);
    }
  };

  const handleClose = useCallback(
    e => {
      yieldToMain(() => {
        onRequestClose && onRequestClose(e);
        if (
          typeof document !== 'undefined' &&
          ipadMode &&
          navigator.userAgent.includes('Mac') &&
          'ontouchend' in document
        ) {
          document.body.classList.add('mobile');
        }
      });
    },
    [ipadMode, onRequestClose]
  );

  const closeButton = hasCloseButton ? (
    <ModalCloseButton className={s['close-button']} />
  ) : null;

  return (
    <ReactModal
      className={c(s['root'], isFullscreen && s['fullscreen'], className)}
      overlayClassName={c(
        s['overlay'],
        useTransparentOverlay && s['transparent-overlay'],
        dontCaptureMouseOverlay && s['no-capture']
      )}
      portalClassName={'ReactModalPortal'}
      style={style}
      contentRef={setContentRef}
      onAfterOpen={onLock}
      onAfterClose={onUnlock}
      onRequestClose={handleClose}
      {...rest}
    >
      <ModalCloseContext.Provider value={handleClose}>
        <PopupContext.Provider
          value={{
            isInPopup: true,
            setScrollableContentRef: setScrollableRef,
            setDefaultScrollableRef: setDefaultScrollRef,
          }}
        >
          {children}
        </PopupContext.Provider>
        {closeButton}
      </ModalCloseContext.Provider>
    </ReactModal>
  );
};
ModalRoot.displayName = 'ModalRoot';

export default ModalRoot;
